import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import SectionWrapper from "@/components/base/SectionWrapper";
import AnimatedCounter from "@/components/base/AnimatedCounter";
import { supabase, type HeroSlide, type Post, type GalleryImage, type Stat } from "@/lib/supabase";

interface SiteSettings {
  mission: string;
  vision: string;
  aboutContent: string;
  pillars: Array<{ title: string; description: string; icon: string }>;
  services: Array<{ title: string; description: string; icon: string }>;
  address: string;
  email: string;
  phone: string;
  facebook: string;
  tiktok: string;
  instagram: string;
  linkedin: string;
}

function parseJSON(val: string | null) {
  try {
    return val ? JSON.parse(val) : [];
  } catch {
    return [];
  }
}

async function getSettings(): Promise<SiteSettings> {
  const { data } = await supabase.from("site_settings").select("key, value");
  const map: Record<string, string> = {};
  (data || []).forEach((s) => { map[s.key] = s.value; });
  return {
    mission: map.mission || "",
    vision: map.vision || "",
    aboutContent: map.aboutContent || "",
    pillars: parseJSON(map.pillars),
    services: parseJSON(map.services),
    address: map.address || "",
    email: map.email || "",
    phone: map.phone || "",
    facebook: map.facebook || "",
    tiktok: map.twitter || "",
    instagram: map.instagram || "",
    linkedin: map.linkedin || "",
  };
}

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [stats, setStats] = useState<Stat[]>([]);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    mission: "", vision: "", aboutContent: "", pillars: [], services: [],
    address: "", email: "", phone: "", facebook: "", tiktok: "", instagram: "", linkedin: "",
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const [{ data: slidesData }, { data: postsData }, { data: galleryData }, { data: statsData }, settings] = await Promise.all([
        supabase.from("hero_slides").select("*").order("display_order", { ascending: true }),
        supabase.from("posts").select("*").eq("status", "published").order("created_at", { ascending: false }).limit(5),
        supabase.from("gallery").select("*").order("date_uploaded", { ascending: false }).limit(6),
        supabase.from("stats").select("*").order("display_order", { ascending: true }),
        getSettings(),
      ]);
      setHeroSlides(slidesData || []);
      setPosts(postsData || []);
      setGalleryImages(galleryData || []);
      setStats(statsData || []);
      setSiteSettings(settings);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % Math.max(heroSlides.length, 1));
  }, [heroSlides.length]);

  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length, nextSlide]);

  const featuredPost = posts[0];
  const regularPosts = posts.slice(1, 5);
  const galleryPreview = galleryImages.slice(0, 6);

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case "event": return "bg-gold-500";
      case "news": return "bg-primary-600";
      default: return "bg-emerald-600";
    }
  };

  const getCategoryLabel = (cat: string) => {
    switch (cat) {
      case "event": return "Event";
      case "news": return "News";
      default: return "Announcement";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-2 border-primary-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* ========== HERO SLIDER ========== */}
      <section className="relative h-[420px] md:h-[600px] lg:h-screen overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? "opacity-100" : "opacity-0"}`}
          >
            <img src={slide.image_url} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
          </div>
        ))}

        {heroSlides.length === 0 && (
          <div className="absolute inset-0 bg-primary-900 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">ACS Student Chapter</h1>
              <p className="text-white/70 text-lg">Rivers State University</p>
            </div>
          </div>
        )}

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center px-4 md:px-8 lg:px-12">
          <div className="w-full max-w-3xl">
            {heroSlides.map((slide, index) => (
              <div
                key={slide.id}
                className={`transition-all duration-1000 ${index === currentSlide ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 absolute"} ${index !== currentSlide ? "pointer-events-none" : ""}`}
              >
                {index === currentSlide && (
                  <>
                    <p className="text-gold-400 text-xs md:text-sm font-medium uppercase tracking-[2px] mb-4">ACS Student Chapter</p>
                    <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-5">{slide.title}</h1>
                    <p className="text-base md:text-lg lg:text-xl text-white/85 leading-relaxed mb-8 max-w-2xl">{slide.subtitle}</p>
                    <Link to={slide.cta_link || "/events"} className="inline-block px-8 md:px-12 py-3.5 md:py-4 bg-gold-600 hover:bg-gold-700 text-white font-medium rounded-lg transition-all duration-300 hover:scale-105 whitespace-nowrap text-sm md:text-base">{slide.cta_text || "Learn More"}</Link>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {heroSlides.length > 1 && (
          <>
            <div className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3">
              {heroSlides.map((_, index) => (
                <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${index === currentSlide ? "bg-gold-500 w-8" : "bg-white/60 hover:bg-white/90"}`} aria-label={`Go to slide ${index + 1}`} />
              ))}
            </div>
            <button onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)} className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm transition-all" aria-label="Previous slide">
              <i className="ri-arrow-left-s-line text-white text-lg md:text-xl" />
            </button>
            <button onClick={nextSlide} className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm transition-all" aria-label="Next slide">
              <i className="ri-arrow-right-s-line text-white text-lg md:text-xl" />
            </button>
          </>
        )}
      </section>

      {/* ========== ABOUT SECTION ========== */}
      <SectionWrapper className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-3">
              <p className="text-gold-600 text-xs font-medium uppercase tracking-[2px] mb-3">About Us</p>
              <h2 className="font-heading text-2xl md:text-4xl lg:text-[42px] font-bold text-primary-900 leading-tight mb-6">Building the Next Generation of Chemists</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-10">{siteSettings.aboutContent}</p>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-5 md:p-6 border-l-4 border-gold-500">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gold-100 text-gold-600"><i className="ri-focus-3-line" /></div>
                    <h3 className="font-heading font-semibold text-base md:text-lg text-primary-900">Our Mission</h3>
                  </div>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">{siteSettings.mission}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-5 md:p-6 border-l-4 border-primary-500">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600"><i className="ri-eye-line" /></div>
                    <h3 className="font-heading font-semibold text-base md:text-lg text-primary-900">Our Vision</h3>
                  </div>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">{siteSettings.vision}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 md:gap-4 mt-8">
                {siteSettings.pillars.map((pillar) => (
                  <div key={pillar.title} className="bg-white border border-gray-100 rounded-xl p-4 md:p-5 hover:border-gold-300 hover:shadow-md transition-all duration-300">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-gold-50 text-gold-600 mb-3">
                      <i className={`${pillar.icon} text-lg md:text-xl`} />
                    </div>
                    <h4 className="font-heading font-semibold text-sm md:text-base text-primary-900 mb-1">{pillar.title}</h4>
                    <p className="text-gray-500 text-xs md:text-sm leading-relaxed">{pillar.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2 relative">
              <div className="relative">
                <img src="https://tmioarhxrauqnrthufdc.supabase.co/storage/v1/object/public/team-images/about.jpeg" alt="Students in the lab" className="w-full rounded-2xl border-[6px] border-white shadow-xl object-cover aspect-[4/5]" />
                <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 w-32 h-32 md:w-40 md:h-40 rounded-xl overflow-hidden border-4 border-white shadow-lg">
                  <img src="https://tmioarhxrauqnrthufdc.supabase.co/storage/v1/object/public/team-images/about2.jpeg" alt="Chemical reaction" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-gold-100 rounded-full opacity-30 -z-10" />
              <div className="absolute top-20 -right-2 w-12 h-12 bg-primary-100 rounded-full opacity-40 -z-10" />
            </div>
          </div>
        </div>
      </SectionWrapper>

      {/* ========== WHAT WE DO ========== */}
      <SectionWrapper className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-gold-600 text-xs font-medium uppercase tracking-[2px] mb-3">Our Activities</p>
            <h2 className="font-heading text-2xl md:text-4xl lg:text-[42px] font-bold text-primary-900">What We Do</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
            {siteSettings.services.map((service) => (
              <div key={service.title} className="group bg-white rounded-2xl p-6 md:p-8 text-center hover:bg-gradient-to-b hover:from-white hover:to-primary-50 hover:-translate-y-3 hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-gold-50 group-hover:bg-gold-100 mx-auto mb-5 md:mb-6 transition-colors">
                  <i className={`${service.icon} text-2xl md:text-3xl text-primary-800 group-hover:scale-110 transition-transform`} />
                </div>
                <h3 className="font-heading font-semibold text-lg md:text-xl text-primary-900 mb-3">{service.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </SectionWrapper>

      {/* ========== STATISTICS ========== */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-primary-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-0">
            {stats.sort((a, b) => a.display_order - b.display_order).map((stat, index) => (
              <div key={stat.id} className={`text-center ${index < stats.length - 1 ? "lg:border-r lg:border-white/20" : ""}`}>
                <p className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-2"><AnimatedCounter value={stat.value} suffix="+" /></p>
                <p className="text-white/70 text-sm md:text-base uppercase tracking-wide">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== NEWS & EVENTS ========== */}
      <SectionWrapper className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <p className="text-gold-600 text-xs font-medium uppercase tracking-[2px] mb-3">Stay Informed</p>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-primary-900">News &amp; Events</h2>
            </div>
            <Link to="/events" className="mt-4 md:mt-0 inline-flex items-center gap-2 text-gold-600 hover:text-gold-700 font-medium text-sm transition-colors">View All<i className="ri-arrow-right-line" /></Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
            {featuredPost && (
              <Link to={`/events/${featuredPost.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 row-span-2 flex flex-col">
                <div className="relative h-56 md:h-72 overflow-hidden">
                  <img src={featuredPost.featured_image} alt={featuredPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-medium text-white ${getCategoryColor(featuredPost.category)}`}>{getCategoryLabel(featuredPost.category)}</span>
                </div>
                <div className="p-5 md:p-6 flex-1 flex flex-col">
                  <p className="text-gray-400 text-xs mb-2">{new Date(featuredPost.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
                  <h3 className="font-heading font-bold text-lg md:text-xl lg:text-2xl text-primary-900 mb-3 group-hover:text-gold-700 transition-colors">{featuredPost.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">{featuredPost.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 text-gold-600 text-sm font-medium group-hover:gap-2.5 transition-all">Read More <i className="ri-arrow-right-line" /></span>
                </div>
              </Link>
            )}

            <div className="grid grid-cols-1 gap-4 md:gap-5">
              {regularPosts.map((post) => (
                <Link key={post.id} to={`/events/${post.slug}`} className="group flex gap-4 bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="w-28 md:w-36 lg:w-40 flex-shrink-0 overflow-hidden">
                    <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="py-3 pr-4 md:py-4 md:pr-5 flex-1 flex flex-col justify-center min-w-0">
                    <span className={`inline-flex w-fit px-2 py-0.5 rounded-full text-[10px] font-medium text-white mb-2 ${getCategoryColor(post.category)}`}>{getCategoryLabel(post.category)}</span>
                    <h4 className="font-heading font-semibold text-sm md:text-base text-primary-900 group-hover:text-gold-700 transition-colors line-clamp-2">{post.title}</h4>
                    <p className="text-gray-400 text-xs mt-1.5">{new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </SectionWrapper>

      {/* ========== GALLERY PREVIEW ========== */}
      <SectionWrapper className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-gold-600 text-xs font-medium uppercase tracking-[2px] mb-3">Moments &amp; Memories</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-primary-900">Our Gallery</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {galleryPreview.map((img, index) => (
              <div key={img.id} className={`relative group overflow-hidden rounded-xl ${index === 0 || index === 5 ? "col-span-2" : ""}`}>
                <img src={img.image_url} alt={img.title} className="w-full h-40 md:h-48 lg:h-56 object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-primary-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center text-center p-4">
                  <p className="text-white font-heading font-semibold text-sm md:text-base mb-1">{img.title}</p>
                  <p className="text-white/80 text-xs">{img.event_name}</p>
                  <Link to="/gallery" className="mt-3 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-gold-500 transition-colors"><i className="ri-zoom-in-line" /></Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/gallery" className="inline-flex items-center gap-2 px-8 py-3 border-2 border-gold-600 text-gold-600 hover:bg-gold-600 hover:text-white font-medium rounded-lg transition-all duration-300 whitespace-nowrap text-sm">Explore Full Gallery<i className="ri-arrow-right-line" /></Link>
          </div>
        </div>
      </SectionWrapper>

      {/* ========== CONTACT SECTION ========== */}
      <SectionWrapper className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 lg:p-10">
              <h3 className="font-heading font-bold text-xl md:text-2xl text-primary-900 mb-2">Get in Touch</h3>
              <p className="text-gray-500 text-sm mb-6">Have a question or want to collaborate? Send us a message.</p>
              <form action="https://form.readdy.ai/submit" method="POST" data-readdy-form className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input type="text" name="name" placeholder="Full Name" required className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" />
                  <input type="email" name="email" placeholder="Email Address" required className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" />
                </div>
                <input type="text" name="subject" placeholder="Subject" required className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" />
                <textarea name="message" placeholder="Your Message" required maxLength={500} rows={5} className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors resize-none" />
                <button type="submit" className="w-full py-3.5 bg-gold-600 hover:bg-gold-700 text-white font-medium rounded-lg transition-colors text-sm">Send Message</button>
              </form>
            </div>

            <div className="bg-primary-900 rounded-2xl p-6 md:p-8 lg:p-10 text-white">
              <h3 className="font-heading font-bold text-xl md:text-2xl mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0"><i className="ri-map-pin-line text-gold-400" /></div>
                  <div><p className="font-medium text-sm mb-1">Address</p><p className="text-white/70 text-sm leading-relaxed">{siteSettings.address}</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0"><i className="ri-mail-line text-gold-400" /></div>
                  <div><p className="font-medium text-sm mb-1">Email</p><a href={`mailto:${siteSettings.email}`} className="text-white/70 text-sm hover:text-gold-400 transition-colors">{siteSettings.email}</a></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0"><i className="ri-phone-line text-gold-400" /></div>
                  <div><p className="font-medium text-sm mb-1">Phone</p><a href={`tel:${siteSettings.phone}`} className="text-white/70 text-sm hover:text-gold-400 transition-colors">{siteSettings.phone}</a></div>
                </div>
              </div>
              <div className="mt-8">
                <p className="font-medium text-sm mb-3">Follow Us</p>
                <div className="flex items-center gap-3">
                  {siteSettings.facebook && <a href={siteSettings.facebook} target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/30 hover:bg-gold-600 hover:border-gold-600 transition-all" aria-label="Facebook"><i className="ri-facebook-fill" /></a>}
                  {siteSettings.tiktok && <a href={siteSettings.tiktok} target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/30 hover:bg-gold-600 hover:border-gold-600 transition-all" aria-label="Twitter"><i className="ri-twitter-x-fill" /></a>}
                  {siteSettings.instagram && <a href={siteSettings.instagram} target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/30 hover:bg-gold-600 hover:border-gold-600 transition-all" aria-label="Instagram"><i className="ri-instagram-line" /></a>}
                  {siteSettings.linkedin && <a href={siteSettings.linkedin} target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/30 hover:bg-gold-600 hover:border-gold-600 transition-all" aria-label="LinkedIn"><i className="ri-linkedin-fill" /></a>}
                </div>
              </div>
              <div className="mt-8 rounded-xl overflow-hidden shadow-lg">
                <iframe src="https://www.google.com/maps/embed?pb=!1m12!1m8!1m3!1d15903.437158247758!2d6.9810679!3d4.7941918!3m2!1i1024!2i768!4f13.1!2m1!1srivers%20state%20university%20on%20google%20maps!5e0!3m2!1sen!2sng!4v1779168948021!5m2!1sen!2sng" width="100%" height="320" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Rivers State University Location" className="rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
}